import { CalendarEvent } from "angular-calendar";
import { addSeconds, endOfWeek, startOfWeek } from 'date-fns';
import { CalendarAdapter } from "../../../adapters/calendar.adapter";
import { User } from "../../../models";
import { ApiService } from "../../../services";
import { UscStoreService } from "../../../services/usc-store/usc-store.service";

export class UscCalendarAdapter extends CalendarAdapter
{
    constructor(
        user: User,
        teamScope: boolean,
        public uscStoreService: UscStoreService,
        public apiService: ApiService
    ) {
        super(user, teamScope);
    }

    public dayEventWidth:number = 1000;

    public handleEvent(action: string, event: CalendarEvent):void {
        var item = this.uscStoreService.getViewItemFromEvent(event);
        this.uscStoreService.setGotoItem(item);
    }

    private isScheduledAppointment(event) {
        return event.e_sub_type != 'Bill Payment' && event.e_sub_type != 'Sales Associate' && event.e_sub_type != 'Account Maintenance';
    }

    public getEvents(params:any):CalendarEvent[] {
        var events = this.uscStoreService.getCalendarEvents();
        if (events) {
            var filteredEvents = [];
            if (params.search_scope == "Team Appointments") {
                events.forEach(e => {
                    if (e.u_team_key_ID.indexOf(this.calendarUser.team_key_ID) >= 0 && this.isScheduledAppointment(e)) {
                        filteredEvents.push(e);
                    }
                })
                events = filteredEvents;
            } else if (params.search_scope == "My Appointments") {
                events.forEach(e => {
                    if (e.u_user_ID == this.calendarUser.ID && this.isScheduledAppointment(e)) {
                        filteredEvents.push(e);
                    }
                })
                events = filteredEvents;
            } else {
                events.forEach(e => {
                    if (this.isScheduledAppointment(e)) {
                        filteredEvents.push(e);
                    }
                })
                events = filteredEvents;
            }
        }
        // if (!this.teamScope && events) {
        //     var filteredEvents = [];
        //     events.forEach(e => {
        //         if (e.u_user_ID == this.calendarUser.ID) {
        //             filteredEvents.push(e);
        //         }
        //     })
        //     events = filteredEvents;
        // }
        return this.generateCalendarEvents(events);
    }

    generateCalendarEvents(events:Array<any>) : CalendarEvent[] {
        if (events == null) return null;
        let calendarEvents:CalendarEvent[] = [];
        for (var i = 0; i < events.length; i++) {
            var e = events[i];
            e.calendar_event = {
                ID: e.e_ID,
                start: new Date(e.e_date),
                end: addSeconds(new Date(e.e_date), e.e_duration_seconds),
                title: this.getEventTitle(e),
                color: this.getEventColor(e)
            }
            if (e.calendar_event.color != this.Color.NotHeld) calendarEvents.push(e.calendar_event);
        };

        return calendarEvents;
    }

    Color:any = {
        Planned: { primary: '#00ff00', secondary: '#e0ffe0', status: 'Planned' },  // green
        Held:    { primary: '#1e90ff', secondary: '#D1E8FF', status: 'Held' },  // blue
        NotHeld: { primary: '#ff0000', secondary: '#FAE3E3', status: 'Not Held' } , // red
        Rescheduled: { primary: '#e3bc08', secondary: '#FDF1BA', status: 'Rescheduled' },  // yellow
        Unassigned: { primary: '#b202b2', secondary: '#e1afe1', status: 'Unassigned' } , // purple
        Default: { primary: '#e3bc08', secondary: '#FDF1BA', status: 'N/A' },  // gray
    }


    getEventColor(e):any {
        if (e.u_title == 'Store') {
            if ((this.uscStoreService.isUSCellCorp() || this.uscStoreService.isUSCellTelesales()) 
                ||
                (this.uscStoreService.isUSCellAgent() && e.dl_account_type == '')) {
                return this.Color.Unassigned;
            }
        }

        switch (e.e_status) {
            case "PL": return this.Color.Planned;
            case "NH": return this.Color.NotHeld;
            case "HD": return (e.e_sub_type == "Rescheduled") ? this.Color.Rescheduled : this.Color.Held;
        }
        return this.Color.Default;
    }

    getEventTitle(e):any {
        var start = new Date(e.e_date);
        var hours = start.getHours();
        var minutes = (start.getMinutes() < 10 ? '0' : '') + start.getMinutes().toString();
        var ampm = (hours >= 12) ? "PM" : "AM";
        if (hours > 12) hours = hours - 12;
        var status = this.getEventColor(e).status;

        var time = `${hours}:${minutes} ${ampm}`;
        var name = e.co_firstname + ' ' + e.co_lastname;
        var phone = this.formatContactNumber(e.co_work_phone1);
        var cm = e.cm_name;
        var firstname = this.teamScope ? `${e.u_firstname}@` : '';
        var e_notes = e.e_notes ? e.e_notes : '[no notes]';
        return (e.cfd_qrfv && e.cfd_qrfv.length > 0)  ? `${firstname}${time}: ${name} (${phone}) - ${cm} - ${e.cfd_qrfv} - ${e.co_type} - (${status}) - ${e_notes}` : `${firstname}${time}: ${name} (${phone}) - ${cm} - ${e.co_type} - (${status})`;
    }
        
    formatContactNumber(phone:string) {
        var formatted = "";
        if (phone && phone.length == 10) {
            formatted = phone.substring(0, 3) + '-' + phone.substring(3, 6) + '-' + phone.substring(6, 10);
        }
        return formatted;
    }

    getCalendarTitle(view:string, date:Date):string {
        var months = [ "January", "Febuary", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December" ];
        var days = [ "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday" ];
        let month:string = months[date.getMonth()]; 
        let day:string = days[date.getDay()]; 
        let week_start:Date = startOfWeek(date);
        let week_end:Date = endOfWeek(date);
        let week_start_month:string = months[week_start.getMonth()];
        let week_start_date:number = week_start.getDate();
        let week_end_month:string = months[week_end.getMonth()];
        let week_end_date:number = week_end.getDate();
        switch (view) {
            case this.Calendar_View_Month: return `${month} ${date.getFullYear()}`;
            case this.Calendar_View_Week: 
                if (week_start_month == week_end_month) return `${week_start_month.substr(0, 3)} ${week_start.getDate()} - ${week_end.getDate()}, ${week_start.getFullYear()}`;
                else return `${week_start_month.substr(0, 3)} ${week_start.getDate()}, ${week_start.getFullYear()} - ${week_end_month.substr(0, 3)} ${week_end.getDate()}, ${week_end.getFullYear()}`;
            case this.Calendar_View_Day: return `${day}, ${month} ${date.getDate()}, ${date.getFullYear()}`;
        }
        return ``;
    }

    getDateStartHour() {
        return 8;
    }

    getDateEndHour() {
        return 20;
    }
}


// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/components/usc-store/usc-calendar-view/usc-calendar-adapter.ts